import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Buildings, MagnifyingGlass, MapPin, Star, Phone, Envelope } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompanyDirectory() {
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCompanies = useCallback(async () => {
    try {
      let url = `${API}/companies?approved_only=true`;
      if (locationFilter) url += `&location=${locationFilter}`;
      if (serviceFilter) url += `&service=${serviceFilter}`;
      
      const { data } = await axios.get(url);
      setCompanies(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  }, [locationFilter, serviceFilter]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const featuredCompany = companies.find(c => c.featured);
  const regularCompanies = filteredCompanies.filter(c => !c.featured);

  return (
    <div className="min-h-screen bg-background_light">
      <Navbar />
      {/* Hero */}
      <div className="bg-primary text-text_light py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Buildings size={48} weight="duotone" />
            <h1 className="text-5xl sm:text-6xl tracking-tight font-semibold">Company Directory</h1>
          </div>
          <p className="text-xl leading-relaxed text-secondary max-w-4xl">
            Find trusted pest control companies in your area. Browse services, pricing, and contact information.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Search & Filters */}
        <div className="bg-white border border-borders rounded-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                data-testid="company-search"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <input
              type="text"
              data-testid="location-filter"
              placeholder="Filter by location..."
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <input
              type="text"
              data-testid="service-filter"
              placeholder="Filter by service..."
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Featured Company */}
        {featuredCompany && (
          <div className="mb-12">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6">Featured Company</h2>
            <div className="bg-accent/10 border-2 border-accent rounded-sm p-8" data-testid="featured-company">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-3xl tracking-tight font-medium mb-2">{featuredCompany.name}</h3>
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin size={20} />
                    <span>{featuredCompany.location}</span>
                  </div>
                  {featuredCompany.bu_number && (
                    <p className="text-sm text-gray-600 mt-1">BU#{featuredCompany.bu_number}</p>
                  )}
                </div>
                <span className="bg-accent text-white px-4 py-2 rounded-sm text-sm uppercase tracking-[0.2em]">
                  Featured
                </span>
              </div>
              
              {featuredCompany.description && (
                <p className="text-base leading-relaxed mb-4">{featuredCompany.description}</p>
              )}
              
              <div className="mb-4">
                <h4 className="font-medium text-sm uppercase tracking-[0.2em] mb-2">Services</h4>
                <div className="flex flex-wrap gap-2">
                  {featuredCompany.services.map((service, idx) => (
                    <span key={service} className="px-3 py-1 bg-white border border-accent text-sm rounded-sm">
                      {service}
                    </span>
                  ))}
                </div>
              </div>
              
              {featuredCompany.pricing_info && (
                <p className="text-base leading-relaxed mb-4">
                  <strong>Pricing:</strong> {featuredCompany.pricing_info}
                </p>
              )}
              
              <div className="flex gap-4">
                <a
                  href={`mailto:${featuredCompany.contact_email}`}
                  className="flex items-center gap-2 bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
                  data-testid="featured-contact-email"
                >
                  <Envelope size={20} />
                  Contact
                </a>
                {featuredCompany.contact_phone && (
                  <a
                    href={`tel:${featuredCompany.contact_phone}`}
                    className="flex items-center gap-2 bg-white border border-primary text-primary px-6 py-3 rounded-sm hover:bg-gray-50 transition-colors"
                  >
                    <Phone size={20} />
                    Call
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* All Companies */}
        <div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6">
            All Companies ({regularCompanies.length})
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : regularCompanies.length === 0 ? (
            <div className="bg-white border border-borders rounded-sm p-8 text-center">
              <p className="text-gray-600">No companies found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularCompanies.map(company => (
                <div key={company.id} className="bg-white border border-borders rounded-sm p-6 hover:-translate-y-1 transition-transform" data-testid={`company-${company.id}`}>
                  <h3 className="text-2xl tracking-tight font-medium mb-2">{company.name}</h3>
                  <div className="flex items-center gap-2 text-gray-600 mb-4">
                    <MapPin size={18} />
                    <span className="text-sm">{company.location}</span>
                  </div>
                  
                  {company.description && (
                    <p className="text-base leading-relaxed mb-4 text-gray-700">{company.description}</p>
                  )}
                  
                  <div className="mb-4">
                    <h4 className="font-medium text-xs uppercase tracking-[0.2em] mb-2">Services</h4>
                    <div className="flex flex-wrap gap-2">
                      {company.services.slice(0, 3).map((service, idx) => (
                        <span key={service} className="px-2 py-1 bg-gray-100 text-xs rounded-sm">
                          {service}
                        </span>
                      ))}
                      {company.services.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-xs rounded-sm">
                          +{company.services.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <a
                    href={`mailto:${company.contact_email}`}
                    className="inline-flex items-center gap-2 text-accent hover:underline text-sm"
                    data-testid={`company-email-${company.id}`}
                  >
                    <Envelope size={16} />
                    Contact
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
